import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import billingService from "../services/billingService";
import logger from "../utils/logger";

const DEFAULT_WORK_SUBS = [
  { name: "Coworking Spaces", id: 6 },
  { name: "Dedicated Desks", id: 5 },
  { name: "Meeting Rooms", id: 4 },
  { name: "Conference Rooms", id: 3 },
  { name: "Private Offices", id: 1 },
];

const DEFAULT_STAY_SUBS = [
  { name: "Hotels", id: 7 },
  { name: "Apartments", id: 8 },
  { name: "Boutique Hotels", id: 9 },
];

const DEFAULT_PLAY_SUBS = [
  { name: "Wellness", id: 10 },
  { name: "Dining", id: 11 },
  { name: "Adventure", id: 12 },
  { name: "Events", id: 13 },
];

const Footer = () => {
  const now = new Date();
  const displayYear = now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear();

  const [workSubcategories, setWorkSubcategories] = useState<any[]>(DEFAULT_WORK_SUBS);
  const [staySubcategories, setStaySubcategories] = useState<any[]>(DEFAULT_STAY_SUBS);
  const [playSubcategories, setPlaySubcategories] = useState<any[]>(DEFAULT_PLAY_SUBS);

  useEffect(() => {
    let isMounted = true;
    // The "all" registry endpoint needs a session, so read each category from the public one
    const listOf = (res: any) => {
      const data = res?.data;
      return Array.isArray(data) ? data : data?.data || data?.content || data?.items || [];
    };

    Promise.all([
      billingService.getPublicServiceSubcategories(1).catch(() => null),
      billingService.getPublicServiceSubcategories(2).catch(() => null),
      billingService.getPublicServiceSubcategories(3).catch(() => null),
    ])
      .then(([work, stay, play]) => {
        if (!isMounted) return;
        const workItems = listOf(work);
        const stayItems = listOf(stay);
        const playItems = listOf(play);

        if (workItems.length > 0) setWorkSubcategories(workItems);
        if (stayItems.length > 0) setStaySubcategories(stayItems);
        if (playItems.length > 0) setPlaySubcategories(playItems);
      })
      .catch(err => {
        logger.warn("Footer: public subcategory registry call failed, using default registry list", err);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <footer className="bg-white border-t border-gray-100 text-gray-800 pt-16 pb-12 px-4 md:px-8">
      <div className="max-w-7xl mx-auto space-y-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Logo & Info */}
          <div className="md:col-span-4 space-y-4">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-blue-600 text-white font-extrabold flex items-center justify-center text-sm">
                S
              </span>
              <span className="text-xl font-extrabold tracking-[0.2em] text-gray-900">SPACIA</span>
            </div>
            <p className="text-xs text-gray-500 max-w-sm leading-relaxed">
              Work, Stay and Play — all in one platform. Live in Kenya and Ghana.
            </p>
            <div>
              <a
                href="https://app.spacia.africa"
                target="_blank"
                rel="noreferrer"
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-5 py-2.5 rounded-full inline-flex items-center gap-1.5 shadow-sm"
              >
                app.spacia.africa ➔
              </a>
            </div>
            <div className="flex gap-3 pt-2">
              <a
                href="https://www.linkedin.com"
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-blue-600 hover:text-white transition-colors"
              >
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                  <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                </svg>
              </a>
              <a
                href="https://www.instagram.com/spaciaafrica/"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-pink-600 hover:text-white transition-colors"
              >
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
              <a
                href="https://twitter.com/SpaciaAfrica_"
                target="_blank"
                rel="noreferrer"
                aria-label="X / Twitter"
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-black hover:text-white transition-colors"
              >
                <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a
                href="https://www.facebook.com/Spacia-Africa-185163956487562"
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-blue-700 hover:text-white transition-colors"
              >
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Links Columns */}
          <div className="md:col-span-8 grid grid-cols-2 sm:grid-cols-6 gap-6">
            {/* WORK */}
            <div className="space-y-3">
              <h5 className="text-xs font-bold text-gray-900 uppercase tracking-wider">WORK</h5>
              <ul className="space-y-2 text-xs font-medium text-gray-500">
                {workSubcategories.map((sub: any) => (
                  <li key={sub.id || sub.name}>
                    <Link
                      to={`/workspaces-search?type=work&categoryId=1${sub.id ? `&subCategoryId=${sub.id}` : ""}`}
                      className="hover:text-blue-600"
                    >
                      {sub.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* STAY */}
            <div className="space-y-3">
              <h5 className="text-xs font-bold text-gray-900 uppercase tracking-wider">STAY</h5>
              <ul className="space-y-2 text-xs font-medium text-gray-500">
                {staySubcategories.map((sub: any) => (
                  <li key={sub.id || sub.name}>
                    <Link
                      to={`/workspaces-search?type=stay&categoryId=2${sub.id ? `&subCategoryId=${sub.id}` : ""}`}
                      className="hover:text-blue-600"
                    >
                      {sub.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* PLAY */}
            <div className="space-y-3">
              <h5 className="text-xs font-bold text-gray-900 uppercase tracking-wider">PLAY</h5>
              <ul className="space-y-2 text-xs font-medium text-gray-500">
                {playSubcategories.map((sub: any) => (
                  <li key={sub.id || sub.name}>
                    <Link
                      to={`/workspaces-search?type=play&categoryId=3${sub.id ? `&subCategoryId=${sub.id}` : ""}`}
                      className="hover:text-blue-600"
                    >
                      {sub.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* MOVE */}
            <div className="space-y-3">
              <div className="flex items-center gap-1">
                <h5 className="text-xs font-bold text-gray-900 uppercase tracking-wider">MOVE</h5>
                <span className="text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-bold">Soon</span>
              </div>
              <ul className="space-y-2 text-xs font-medium text-gray-500">
                <li>
                  <Link to="/move" className="hover:text-blue-600">
                    Accredited Car Rentals
                  </Link>
                </li>
              </ul>
            </div>

            {/* EAT */}
            <div className="space-y-3">
              <div className="flex items-center gap-1">
                <h5 className="text-xs font-bold text-gray-900 uppercase tracking-wider">EAT</h5>
                <span className="text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-bold">Soon</span>
              </div>
              <ul className="space-y-2 text-xs font-medium text-gray-500">
                <li>
                  <span className="text-gray-400">Food Orders</span>
                </li>
              </ul>
            </div>

            {/* COMPANY */}
            <div className="space-y-3">
              <h5 className="text-xs font-bold text-gray-900 uppercase tracking-wider">COMPANY</h5>
              <ul className="space-y-2 text-xs font-medium text-gray-500">
                <li>
                  <Link to="/platform" className="hover:text-blue-600">
                    About SPACIA
                  </Link>
                </li>
                <li>
                  <a
                    href="https://softtribe.com/about"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-blue-600"
                  >
                    About theSOFTtribe
                  </a>
                </li>
                <li>
                  <Link to="/enterprise-ready" className="hover:text-blue-600">
                    Ready for the Enterprise
                  </Link>
                </li>
                <li>
                  <a href="mailto:sales@spacia.africa" className="hover:text-blue-600">
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-400">
          <div>
            <span className="font-extrabold text-gray-900 tracking-widest mr-2">SPACIA</span>
            <span>© {displayYear} SPACIA Ltd. Developed by theSOFTtribe.</span>
          </div>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="hover:text-gray-600">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="hover:text-gray-600">
              Terms of Use
            </Link>
            <Link to="/trust" className="hover:text-gray-600 font-bold text-gray-700">
              Trust & Safety
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
